import React, { useState, useMemo } from "react";
import {
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    InputAdornment,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Badge,
    useTheme,
    useMediaQuery
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { TRASPASO_ESTADO, TRASPASO_ESTADO_LABELS } from "../../constants";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX, ALTURA_BOTON } from "../../../../components/ui/buttonStyles";

/**
 * Formulario de filtros para traspasos con colapso en mobile
 */
export default function FiltrosTraspaso({
    fechaInicio,
    setFechaInicio,
    fechaFin,
    setFechaFin,
    almacen,
    setAlmacen,
    empleado,
    setEmpleado,
    q,
    setQ,
    almacenesOptions,
    empleadosOptions,
    onFiltrar,
    onLimpiar,
    loading,
    totalResultados,
    fechaInicioInicial,
    fechaFinInicial
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const [expanded, setExpanded] = useState(true);

    // Contador de filtros activos (excluyendo búsqueda)
    const filtrosActivos = useMemo(() => {
        let count = 0;
        if (fechaInicio) count++;
        if (fechaFin) count++;
        if (almacen) count++;
        if (empleado) count++;
        return count;
    }, [fechaInicio, fechaFin, almacen, empleado]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            onFiltrar();
        }
    };

    const handleLimpiar = () => {
        if (onLimpiar) onLimpiar();
    };

    const filtrosContent = (
        <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-end" }}
        >
            {/* Fecha inicio */}
            <Box sx={{ flex: 1 }}>
                <TextField
                    type="date"
                    label="Fecha inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                        },
                    }}
                />
            </Box>

            {/* Fecha fin */}
            <Box sx={{ flex: 1 }}>
                <TextField
                    type="date"
                    label="Fecha fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={fechaFin && fechaInicio && fechaFin < fechaInicio}
                    helperText={fechaFin && fechaInicio && fechaFin < fechaInicio ? "Debe ser posterior a fecha inicio" : ""}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                        },
                    }}
                />
            </Box>

            {/* Almacén */}
            <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                    <InputLabel shrink>Almacén</InputLabel>
                    <Select
                        displayEmpty
                        notched
                        value={almacen}
                        onChange={(e) => setAlmacen(e.target.value)}
                        label="Almacén"
                        sx={{
                            backgroundColor: "#fff",
                        }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {almacenesOptions.map((a) => (
                            <MenuItem key={a.CODIGO} value={a.CODIGO}>
                                {a.NOMBRE}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Empleado */}
            <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                    <InputLabel shrink>Empleado</InputLabel>
                    <Select
                        displayEmpty
                        notched
                        value={empleado}
                        onChange={(e) => setEmpleado(e.target.value)}
                        label="Empleado"
                        sx={{
                            backgroundColor: "#fff",
                        }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {empleadosOptions.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id}>
                                {emp.username}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Buscar */}
            <Box sx={{ flex: 1 }}>
                <TextField
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="ID, artículo, barras..."
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#6c757d" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                        },
                    }}
                />
            </Box>

            {/* Botón Filtrar */}
            <Box sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { md: 120 } }}>
                <Button
                    variant="contained"
                    onClick={onFiltrar}
                    disabled={loading}
                    fullWidth
                    startIcon={
                        loading ? (
                            <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                            <Badge badgeContent={filtrosActivos} color="error">
                                <FilterListIcon />
                            </Badge>
                        )
                    }
                    sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                >
                    Filtrar
                </Button>
            </Box>

            {/* Botón Limpiar */}
            <Box sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { md: 120 } }}>
                <Button
                    variant="outlined"
                    onClick={handleLimpiar}
                    disabled={loading}
                    fullWidth
                    startIcon={<ClearIcon />}
                    sx={{ ...BOTON_SECUNDARIO_SX, height: ALTURA_BOTON.FILTRO }}
                >
                    Limpiar
                </Button>
            </Box>
        </Stack>
    );

    // En mobile: Accordion colapsable
    if (isMobile) {
        return (
            <Box>
                <Accordion
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}
                    sx={{
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                        mb: 2
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: expanded ? "8px 8px 0 0" : "8px",
                            "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 1
                            }
                        }}
                    >
                        <FilterListIcon sx={{ color: "#cc071e" }} />
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>
                            Filtros
                        </Typography>
                        {filtrosActivos > 0 && (
                            <Badge badgeContent={filtrosActivos} color="error" sx={{ mr: 2 }} />
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 3 }}>
                        {filtrosContent}
                        {totalResultados !== undefined && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 2, textAlign: "center" }}
                            >
                                {totalResultados} {totalResultados === 1 ? 'resultado' : 'resultados'}
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    }

    // En desktop: Siempre visible
    return (
        <Box>
            {filtrosContent}
            {totalResultados !== undefined && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, textAlign: "right" }}
                >
                    {totalResultados} {totalResultados === 1 ? 'resultado' : 'resultados'}
                </Typography>
            )}
        </Box>
    );
}
